import {
  type ApprovalProposalAction,
  type ApprovalRejectionReason,
  type ApprovalSubmitRequest,
  ApprovalSubmitRequestSchema,
} from "@adpilot/shared";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitApprovalProposal } from "@/hooks/use-approvals";
import { getApiErrorMessage } from "@/lib/api-error";
import { localizedTargetLabel } from "@/lib/target-display";

type DraftDecision = "approve" | "reject" | "modify_approve";

export interface ApprovalActionDraft {
  decision: DraftDecision;
  reasonCode: ApprovalRejectionReason | null;
  note: string;
  modifiedBid: string;
}

const decisionValues: DraftDecision[] = ["approve", "reject", "modify_approve"];
const rejectionReasonValues: ApprovalRejectionReason[] = [
  "strategic_keyword",
  "core_keyword_protection",
  "insufficient_data",
  "external_promotion",
  "inventory_reason",
  "wrong_interpretation",
  "manual_override",
  "other",
];

export function createDefaultApprovalDraft(
  actions: readonly ApprovalProposalAction[],
): Record<string, ApprovalActionDraft> {
  return Object.fromEntries(
    actions
      .filter(({ reviewStatus }) => reviewStatus === "pending")
      .map((action) => [
        action.id,
        {
          decision: "approve",
          reasonCode: null,
          note: "",
          modifiedBid: action.newBid?.toFixed(2) ?? "",
        },
      ]),
  );
}

export function buildApprovalSubmitRequest(
  actions: readonly ApprovalProposalAction[],
  draft: Record<string, ApprovalActionDraft>,
) {
  const outcomes = actions
    .filter(({ reviewStatus }) => reviewStatus === "pending")
    .map((action) => {
      const item = draft[action.id];
      if (!item) return { actionId: action.id, decision: "approve" as const };
      if (item.decision === "reject") {
        return {
          actionId: action.id,
          decision: "reject" as const,
          ...(item.reasonCode ? { reasonCode: item.reasonCode } : {}),
          ...(item.note.trim() ? { note: item.note.trim() } : {}),
        };
      }
      if (item.decision === "modify_approve") {
        return {
          actionId: action.id,
          decision: "modify_approve" as const,
          modifiedBid: Number(item.modifiedBid),
          ...(item.note.trim() ? { note: item.note.trim() } : {}),
        };
      }
      return {
        actionId: action.id,
        decision: "approve" as const,
        ...(item.note.trim() ? { note: item.note.trim() } : {}),
      };
    });
  return ApprovalSubmitRequestSchema.safeParse({ actions: outcomes });
}

export function highRiskApprovals(
  actions: readonly ApprovalProposalAction[],
  request: ApprovalSubmitRequest,
): string[] {
  const byId = new Map(actions.map((action) => [action.id, action]));
  return [
    ...new Set(
      request.actions.flatMap((outcome) => {
        const action = byId.get(outcome.actionId);
        return outcome.decision !== "reject" &&
          (action?.riskLevel === "high" || action?.riskLevel === "critical")
          ? [action.targetLabel]
          : [];
      }),
    ),
  ];
}

interface ReviewProposalDialogProps {
  proposalId: string;
  shopId?: string;
  actions: ApprovalProposalAction[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied: () => void;
}

export function ReviewProposalDialog({
  proposalId,
  shopId,
  actions,
  open,
  onOpenChange,
  onApplied,
}: ReviewProposalDialogProps) {
  const { t } = useTranslation("approval");
  const decisionOptions = decisionValues.map((value) => ({
    value,
    label: t(`decision.${value}`),
  }));
  const nonBidDecisionOptions = decisionOptions.filter(({ value }) => value !== "modify_approve");
  const rejectionReasons: { value: ApprovalRejectionReason | null; label: string }[] = [
    { value: null, label: t("reviewDialog.noReason") },
    ...rejectionReasonValues.map((value) => ({
      value,
      label: t(`rejectReason.${value}`),
    })),
  ];
  const pending = actions.filter(({ reviewStatus }) => reviewStatus === "pending");
  const [draft, setDraft] = useState(() => createDefaultApprovalDraft(actions));
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<ApprovalSubmitRequest | null>(null);
  const [criticalTargets, setCriticalTargets] = useState<string[]>([]);
  const mutation = useSubmitApprovalProposal();
  const outcomeCounts = pending.reduce(
    (counts, action) => {
      const decision = draft[action.id]?.decision ?? "approve";
      counts[decision]++;
      return counts;
    },
    { approve: 0, reject: 0, modify_approve: 0 },
  );

  function patchDraft(actionId: string, patch: Partial<ApprovalActionDraft>): void {
    setDraft((current) => ({
      ...current,
      [actionId]: { ...current[actionId]!, ...patch },
    }));
  }

  async function submitRequest(body: ApprovalSubmitRequest): Promise<void> {
    try {
      await mutation.mutateAsync({ proposalId, body, shopId });
      onOpenChange(false);
      onApplied();
    } catch (mutationError) {
      setError(await getApiErrorMessage(mutationError));
    }
  }

  function review(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const parsed = buildApprovalSubmitRequest(actions, draft);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("reviewDialog.validationFallback"));
      return;
    }
    setError(null);
    const highRisk = highRiskApprovals(actions, parsed.data).map((label) =>
      localizedTargetLabel(label, t),
    );
    if (highRisk.length > 0) {
      setRequest(parsed.data);
      setCriticalTargets(highRisk);
      return;
    }
    void submitRequest(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) onOpenChange(nextOpen);
      }}
    >
      <DialogPopup className="max-w-2xl" closeProps={{ disabled: mutation.isPending }}>
        <DialogHeader>
          <DialogTitle>
            {request ? t("reviewDialog.titleConfirm") : t("reviewDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {request ? t("reviewDialog.descriptionConfirm") : t("reviewDialog.description")}
          </DialogDescription>
        </DialogHeader>
        {request ? (
          <DialogPanel>
            <ul className="flex list-disc flex-col gap-2 pl-5 text-sm">
              {criticalTargets.map((target) => (
                <li key={target}>{target}</li>
              ))}
            </ul>
          </DialogPanel>
        ) : (
          <form className="flex min-h-0 flex-1 flex-col" onSubmit={review}>
            <DialogPanel className="flex flex-col gap-4">
              {pending.map((action) => {
                const item = draft[action.id]!;
                const canModify =
                  action.actionType === "increase_bid" || action.actionType === "decrease_bid";
                const availableDecisionOptions = canModify
                  ? decisionOptions
                  : nonBidDecisionOptions;
                return (
                  <FieldSet
                    key={action.id}
                    className="gap-3 rounded-xl border border-border p-4"
                    disabled={mutation.isPending}
                  >
                    <FieldLegend variant="label" className="px-1">
                      {localizedTargetLabel(action.targetLabel, t)} ·{" "}
                      {t(`filters.risk.${action.riskLevel}`)}
                    </FieldLegend>
                    <FieldGroup className="gap-3">
                      <Field>
                        <FieldLabel>{t("reviewDialog.outcome")}</FieldLabel>
                        <Select
                          items={availableDecisionOptions}
                          value={item.decision}
                          onValueChange={(value) => patchDraft(action.id, { decision: value! })}
                        >
                          <SelectTrigger
                            className="w-full"
                            aria-label={t("reviewDialog.outcomeAria", {
                              target: localizedTargetLabel(action.targetLabel, t),
                            })}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {availableDecisionOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </Field>
                      {item.decision === "reject" && (
                        <Field>
                          <FieldLabel>{t("rejectDialog.reasonLabel")}</FieldLabel>
                          <Select
                            items={rejectionReasons}
                            value={item.reasonCode}
                            onValueChange={(value) =>
                              patchDraft(action.id, {
                                reasonCode: value as ApprovalRejectionReason | null,
                              })
                            }
                          >
                            <SelectTrigger
                              className="w-full"
                              aria-label={t("reviewDialog.reasonAria", {
                                target: localizedTargetLabel(action.targetLabel, t),
                              })}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {rejectionReasons.map((reason) => (
                                  <SelectItem key={reason.value ?? "none"} value={reason.value}>
                                    {reason.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                      {item.decision === "modify_approve" && (
                        <Field>
                          <FieldLabel htmlFor={`bid-${action.id}`}>
                            {t("reviewDialog.approvedBid")}
                          </FieldLabel>
                          <Input
                            id={`bid-${action.id}`}
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.modifiedBid}
                            onChange={(event) =>
                              patchDraft(action.id, { modifiedBid: event.target.value })
                            }
                          />
                        </Field>
                      )}
                      <Field>
                        <FieldLabel htmlFor={`note-${action.id}`}>
                          {t("reviewDialog.note")}
                        </FieldLabel>
                        <Textarea
                          id={`note-${action.id}`}
                          value={item.note}
                          onChange={(event) => patchDraft(action.id, { note: event.target.value })}
                          maxLength={2000}
                        />
                      </Field>
                    </FieldGroup>
                  </FieldSet>
                );
              })}
              <p className="text-sm text-muted-foreground">
                {t("reviewDialog.summary", {
                  approve: outcomeCounts.approve,
                  reject: outcomeCounts.reject,
                  modify: outcomeCounts.modify_approve,
                })}
              </p>
              {error && <FieldError>{error}</FieldError>}
            </DialogPanel>
            <DialogFooter>
              <DialogClose
                render={<Button variant="outline">{t("common:actions.cancel")}</Button>}
              />
              <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
                {t("reviewDialog.submit", { count: pending.length })}
              </Button>
            </DialogFooter>
          </form>
        )}
        {request && (
          <DialogFooter>
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => {
                setRequest(null);
                setCriticalTargets([]);
              }}
            >
              {t("common:actions.back")}
            </Button>
            <Button
              loading={mutation.isPending}
              disabled={mutation.isPending}
              onClick={() => void submitRequest(request)}
            >
              {t("reviewDialog.confirmExecute")}
            </Button>
          </DialogFooter>
        )}
      </DialogPopup>
    </Dialog>
  );
}
